import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Plus, Minus, Tag, Truck, Globe, Check } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useStore } from '../context/StoreContext';
import { useLocale } from '../context/LocaleContext';
import { VAT_RATES } from '../i18n/translations';
import { validateCoupon } from '../api/client';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';

const EmptyBag = ({ t }) => (
  <div className="min-h-screen bg-white">
    <Header />
    <div className="max-w-2xl mx-auto text-center py-20 px-4">
      <div className="w-24 h-24 rounded-full bg-gray-100 mx-auto flex items-center justify-center mb-4">
        <span className="text-5xl">🛒</span>
      </div>
      <h2 className="text-2xl font-black">{t('emptyBag')}</h2>
      <p className="text-gray-600 mt-2">{t('emptyBagSub')}</p>
      <Link to="/" className="inline-block mt-6 bg-[#22c55e] text-white px-8 py-3 font-bold tracking-wider text-sm hover:bg-[#16a34a] transition">{t('continueShopping')}</Link>
    </div>
    <Footer />
  </div>
);

const Cart = () => {
  const { cart, updateQty, removeFromCart, cartTotal, cartMrpTotal, cartTotalEUR, cartMrpTotalEUR } = useStore();
  const { t, formatPrice, productPrice, freeShipThresholdINR, shippingFeeINR, currency, vatRate, vatCountryName, detectedCountry, setDetectedCountry } = useLocale();
  const [coupon, setCoupon] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponInfo, setCouponInfo] = useState(null); // {code, percent, description}
  const [couponError, setCouponError] = useState('');
  const [applying, setApplying] = useState(false);

  // When shopper is browsing in EUR AND every item has an admin-set priceEUR,
  // display the EUR totals directly (never FX-convert the INR base). Otherwise
  // fall back to INR × FX via formatPrice.
  const useEurNative = currency === 'EUR' && cartTotalEUR != null;
  const showAmount = (inrAmount, eurAmount) => {
    if (useEurNative && eurAmount != null) return `€${Number(eurAmount).toFixed(2)}`;
    return formatPrice(inrAmount);
  };

  const applyCoupon = async () => {
    const code = coupon.trim();
    if (!code) return;
    setApplying(true);
    setCouponError('');
    try {
      const res = await validateCoupon(code, cartTotal, currency);
      if (res && res.ok) {
        setCouponDiscount(Number(res.discount || 0));
        setCouponInfo({ code: res.code, percent: res.percent, description: res.description });
        toast({ title: t('couponApplied'), description: `${res.code} · ${res.percent}% off` });
      } else {
        setCouponDiscount(0);
        setCouponInfo(null);
        setCouponError(res?.error || t('invalidCoupon'));
      }
    } catch (err) {
      setCouponDiscount(0);
      setCouponInfo(null);
      setCouponError(err.response?.data?.detail || err.message);
    } finally {
      setApplying(false);
    }
  };

  const removeCoupon = () => {
    setCoupon('');
    setCouponDiscount(0);
    setCouponInfo(null);
    setCouponError('');
  };

  // Shipping is always free — the cost is already priced into the product.
  const freeShipping = true;
  const shipping = 0;
  const savings = cartMrpTotal - cartTotal + couponDiscount;
  const finalTotal = Math.max(0, cartTotal - couponDiscount + shipping);

  // Sorted list of countries for the VAT preview picker (EUR-only)
  const countryOptions = Object.entries(VAT_RATES)
    .filter(([code]) => code !== 'IN' && code !== 'US' && code !== 'MX')
    .map(([code, v]) => ({ code, name: v.name, rate: v.rate }))
    .sort((a, b) => a.name.localeCompare(b.name));

  if (!cart || cart.length === 0) return <EmptyBag t={t} />;

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <Header />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <h1 className="text-2xl font-black mb-6">{t('myBag')} <span className="text-gray-500 font-normal text-lg">({cart.length} {t('items')})</span></h1>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-3">
            <div className="bg-white p-3 rounded flex items-center gap-2 text-sm border">
              <Truck size={16} className="text-green-600" />
              <span>{t('freeShipTag')}</span>
            </div>
            {cart.map((item) => (
              <div key={item.itemId} className="bg-white p-4 rounded flex gap-4 border">
                <Link to={`/product/${item.productId}`} className="w-24 h-32 shrink-0 bg-gray-100 overflow-hidden">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                </Link>
                <div className="flex-1">
                  <h3 className="font-semibold text-sm">{item.name}</h3>
                  <p className="text-xs text-gray-500 mt-1">{t('size')}: {item.size}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="font-bold">{productPrice(item, 'price')}</span>
                    {item.mrp > item.price && <span className="text-xs text-gray-400 line-through">{productPrice(item, 'mrp')}</span>}
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center border rounded">
                      <button onClick={() => updateQty(item.itemId, item.qty - 1)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100"><Minus size={14} /></button>
                      <span className="px-3 text-sm font-semibold">{item.qty}</span>
                      <button onClick={() => updateQty(item.itemId, item.qty + 1)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100"><Plus size={14} /></button>
                    </div>
                    <button onClick={() => removeFromCart(item.itemId)} className="text-gray-500 hover:text-[#22c55e] transition flex items-center gap-1 text-xs">
                      <Trash2 size={14} /> {t('remove')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <aside className="lg:col-span-1">
            <div className="bg-white p-5 rounded border sticky top-24">
              <h3 className="font-bold text-sm tracking-wider border-b pb-3">{t('priceDetails')} ({cart.length} {t('items')})</h3>
              <div className="space-y-2 text-sm py-3">
                <div className="flex justify-between"><span>{t('totalMrp')}</span><span>{showAmount(cartMrpTotal, cartMrpTotalEUR)}</span></div>
                <div className="flex justify-between text-green-600"><span>{t('discountMrp')}</span><span>- {showAmount(cartMrpTotal - cartTotal, (cartMrpTotalEUR != null && cartTotalEUR != null) ? (cartMrpTotalEUR - cartTotalEUR) : null)}</span></div>
                {couponDiscount > 0 && <div className="flex justify-between text-green-600"><span>{t('couponDiscount')}</span><span>- {formatPrice(couponDiscount)}</span></div>}
                <div className="flex justify-between"><span>{t('shipping')}</span><span className={shipping === 0 ? 'text-green-600' : ''}>{shipping === 0 ? t('free') : formatPrice(shipping)}</span></div>
              </div>
              <div className="border-t pt-3 flex justify-between font-black text-base">
                <span>{t('totalAmount')}</span><span>{showAmount(finalTotal, useEurNative ? Math.max(0, (cartTotalEUR || 0) - couponDiscount + (shipping ? 6 : 0)) : null)}</span>
              </div>
              {currency === 'EUR' && (
                <div className="mt-2 mb-1 flex items-center gap-2 border rounded px-2 py-1.5 bg-white text-xs">
                  <Globe size={13} className="text-[#22c55e] shrink-0" />
                  <label htmlFor="cart-country-picker" className="text-gray-600 shrink-0">Ship to</label>
                  <select
                    id="cart-country-picker"
                    data-testid="cart-country-picker"
                    value={detectedCountry && countryOptions.some((c) => c.code === detectedCountry) ? detectedCountry : ''}
                    onChange={(e) => setDetectedCountry(e.target.value || null)}
                    className="flex-1 bg-transparent outline-none font-semibold text-black cursor-pointer"
                  >
                    <option value="">Auto (EU default)</option>
                    {countryOptions.map((c) => (
                      <option key={c.code} value={c.code}>{`${c.name} · VAT ${c.rate}%`}</option>
                    ))}
                  </select>
                </div>
              )}
              {currency === 'EUR' && vatRate > 0 && (
                <div className="mt-1 flex justify-between text-[11px] text-gray-500 border-l-2 border-[#22c55e]/40 pl-2 py-1 bg-green-50/50 rounded-r" data-testid="cart-vat-line">
                  <span>{vatCountryName ? `${vatCountryName} · ` : ''}{t('vatLine', { rate: vatRate })} · already included</span>
                  <span>{formatPrice(Math.round((cartTotal - couponDiscount) * vatRate / (100 + vatRate)))}</span>
                </div>
              )}
              {savings > 0 && <p className="text-xs text-green-600 font-semibold mt-2">{t('savingMsg', { amount: formatPrice(savings) })}</p>}

              <div className="mt-4">
                {couponInfo ? (
                  <div className="flex items-center justify-between border-2 border-[#22c55e] bg-green-50 rounded px-3 py-2" data-testid="cart-coupon-applied">
                    <div className="flex items-center gap-2 text-xs">
                      <Check size={14} className="text-[#22c55e]" />
                      <span className="font-bold">{couponInfo.code}</span>
                      <span className="text-gray-600">· {couponInfo.percent}% off · {couponInfo.description}</span>
                    </div>
                    <button
                      onClick={removeCoupon}
                      className="text-[11px] text-gray-500 hover:text-red-600 font-bold"
                      data-testid="cart-coupon-remove"
                    >
                      REMOVE
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <div className="flex items-center flex-1 border rounded px-2">
                      <Tag size={14} className="text-gray-400" />
                      <input
                        value={coupon}
                        onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyCoupon(); } }}
                        placeholder={t('couponPlaceholder')}
                        className="flex-1 outline-none text-xs px-2 py-2"
                        data-testid="cart-coupon-input"
                      />
                    </div>
                    <button
                      onClick={applyCoupon}
                      disabled={applying || !coupon.trim()}
                      className="text-xs font-bold text-[#22c55e] tracking-wider disabled:opacity-40"
                      data-testid="cart-coupon-apply"
                    >
                      {applying ? '…' : t('apply')}
                    </button>
                  </div>
                )}
                {couponError && (
                  <p className="text-[11px] text-red-600 mt-1.5 font-semibold" data-testid="cart-coupon-error">{couponError}</p>
                )}
              </div>

              <Button onClick={() => toast({ title: t('checkoutDemo'), description: t('checkoutDemoSub') })} className="w-full mt-5 h-12 bg-[#22c55e] hover:bg-[#16a34a] font-bold tracking-wider">{t('placeOrder')}</Button>
            </div>
          </aside>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Cart;
